<template>
  <section>
    <div class="panel">
      <div class="w-full mb-4">
        <div class="flex w-full">
          <p class="content">
            <b>{{ $t('order_field') }}</b>
          </p>
        </div>
        <div class="flex w-full">
          <b-radio v-model="orderField" native-value="name">
            {{ $t('name') }}
          </b-radio>
          <b-radio v-model="orderField" native-value="code">
            {{ $t('code') }}
          </b-radio>
        </div>
      </div>
      <div class="w-full mb-4">
        <div class="flex w-full">
          <p class="content">
            <b>{{ $t('order_dir') }}</b>
          </p>
        </div>
        <div class="flex w-full">
          <b-radio v-model="orderDir" native-value="asc">
            {{ $t('ascending') }}
          </b-radio>
          <b-radio v-model="orderDir" native-value="desc">
            {{ $t('descending') }}
          </b-radio>
        </div>
      </div>
      <div class="w-full mb-4">
        <div class="flex w-full">
          <p class="content">
            <b>{{ $t('export_template') }}</b>
          </p>
        </div>
        <div class="flex w-full">
          <b-field class="mr-2">
            <b-select v-model="template" icon="file-export" icon-pack="fas" @input="onTemplateChange">
              <option value="default">{{ $t('default_template') }}</option>
              <option value="simple">{{ $t('plain_template') }}</option>
              <option value="resources_breakdown">{{ $t('resources_breakdown') }}</option>
            </b-select>
          </b-field>
        </div>
      </div>

      <!-- Selectores en cascada para filtrar la exportacion por capitulo etiqueta a cualquier nivel.
           Disciplina (siempre visible si hay tag chapters) → Pestaña (aparece tras elegir Disciplina con hijos)
           → Equipo (aparece tras elegir Pestaña con hijos). El chapter_id enviado al backend es el del nivel
           mas profundo seleccionado (o null si todos quedan en "Todas"). -->
      <div v-if="template === 'resources_breakdown' && chaptersTree.length" class="w-full mb-4">
        <!-- Una sola fila horizontal. overflow-x-auto evita romper layout si el nombre del equipo es muy largo. -->
        <div class="flex items-end" style="gap: 16px; overflow-x: auto;">
          <!-- Disciplina (nivel 1) -->
          <div style="min-width: 220px;">
            <p class="content mb-1"><b>{{ $t('discipline') }}</b></p>
            <b-field>
              <b-select v-model="selectedDisciplina" icon="layer-group" icon-pack="fas" @input="onDisciplinaChange">
                <option :value="null">{{ $t('all') }}</option>
                <option v-for="d in chaptersTree" :key="'d_' + d.id" :value="d">
                  {{ d.name }}<span v-if="d.assignments_total"> ({{ d.assignments_total }})</span>
                </option>
              </b-select>
            </b-field>
          </div>

          <!-- Pestaña Excel (nivel 2) -->
          <div v-if="selectedDisciplina && pestanaOptions.length" style="min-width: 220px;">
            <p class="content mb-1"><b>{{ $t('tag_chapters_level_2') }}</b></p>
            <b-field>
              <b-select v-model="selectedPestana" icon="file-alt" icon-pack="fas" @input="onPestanaChange">
                <option :value="null">{{ $t('all') }}</option>
                <option v-for="p in pestanaOptions" :key="'p_' + p.id" :value="p">
                  {{ p.name }}<span v-if="p.assignments_total"> ({{ p.assignments_total }})</span>
                </option>
              </b-select>
            </b-field>
          </div>

          <!-- Equipo (nivel 3) -->
          <div v-if="selectedPestana && equipoOptions.length" style="min-width: 240px;">
            <p class="content mb-1"><b>{{ $t('tag_chapters_level_3') }}</b></p>
            <b-field>
              <b-select v-model="selectedEquipo" icon="users-cog" icon-pack="fas">
                <option :value="null">{{ $t('all') }}</option>
                <option v-for="e in equipoOptions" :key="'e_' + e.id" :value="e">
                  {{ e.name }}<span v-if="e.assignments_total"> ({{ e.assignments_total }})</span>
                </option>
              </b-select>
            </b-field>
          </div>
        </div>
      </div>

      <div class="flex w-full">
        <v-button
          type="button"
          color="green"
          icon="file-export"
          :disabled="loading"
          @click="exportActivities"
        >
          <span v-if="!loading">{{ $t('export') }}</span>
          <span v-else>
            <i class="fa fa-spinner fa-spin"></i> {{ $t('loading') }}
          </span>
        </v-button>
      </div>
    </div>
  </section>
</template>

<script>
  import axios from 'axios'

  export default {
    name: 'export-assignments',

    props: {
      project_id: {type: Number, default: 0},
    },

    data: () => ({
      orderField: 'name',
      orderDir: 'asc',
      template: 'default',
      loading: false,
      chaptersTree: [],
      selectedDisciplina: null,
      selectedPestana: null,
      selectedEquipo: null,
    }),

    computed: {
      pestanaOptions() {
        return this.selectedDisciplina && Array.isArray(this.selectedDisciplina.children)
          ? this.selectedDisciplina.children : [];
      },
      equipoOptions() {
        return this.selectedPestana && Array.isArray(this.selectedPestana.children)
          ? this.selectedPestana.children : [];
      },
      effectiveChapterId() {
        if (this.selectedEquipo) return this.selectedEquipo.id;
        if (this.selectedPestana) return this.selectedPestana.id;
        if (this.selectedDisciplina) return this.selectedDisciplina.id;
        return null;
      },
    },

    methods: {
      // Cuando el usuario elige "Recursos descompuesto" cargamos por unica vez el arbol completo de capitulos etiqueta.
      async onTemplateChange(value) {
        this.resetChapterSelection();
        if (value === 'resources_breakdown' && this.chaptersTree.length === 0) {
          try {
            const { data } = await axios.get(`/api/v2/project/${this.project_id}/tag-chapters/tree`);
            if (data && data.success) {
              this.chaptersTree = data.chapters || [];
            }
          } catch (e) {
          }
        }
      },

      onDisciplinaChange() {
        this.selectedPestana = null;
        this.selectedEquipo = null;
      },
      onPestanaChange() {
        this.selectedEquipo = null;
      },
      resetChapterSelection() {
        this.selectedDisciplina = null;
        this.selectedPestana = null;
        this.selectedEquipo = null;
      },

      exportActivities() {
         this.loading = true;
        let params = {
          projectId: this.project_id,
          orderDir: this.orderDir,
          orderField: this.orderField,
          template: this.template,
        };
        if (this.template === 'resources_breakdown' && this.effectiveChapterId) {
          params.chapterId = this.effectiveChapterId;
        }

        var self = this;

        axios({
          method: 'get',
          url: '/api/v2/export/assignment',
          responseType: 'blob',
          params: params
        })
          .then(function (response) {
            self.$notify.success('success_export');
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.download = self.$t('assignments') + '.xlsx';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          })
          .catch(error => {
            self.$notify.error('error_export');
            console.log(error)
          })
          .finally(() => {
            this.loading = false;
          });
      }
    },
  };

</script>
